// Display formats from the design system (Content fundamentals → Time and numbers).

export type Locale = 'de' | 'en';

const pad = (n: number): string => (n < 10 ? `0${n}` : `${n}`);

/** Live clock `H:MM:SS` ("1:27:40"). Truncates to whole seconds. */
export function formatClock(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 3600)}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
}

/** Duration: `H:MM h` from one hour ("2:14 h"), minutes below ("45 min"). Rounded to the nearest minute. */
export function formatDuration(ms: number): string {
  const minutes = Math.max(0, Math.round(ms / 60_000));
  const hours = Math.floor(minutes / 60);
  return hours > 0 ? `${hours}:${pad(minutes % 60)} h` : `${minutes} min`;
}

/** Decimal hours with the locale's separator ("38,5 h" / "38.5 h"). */
export function formatDecimalHours(ms: number, locale: Locale, fractionDigits = 1): string {
  const value = new Intl.NumberFormat(locale === 'de' ? 'de-DE' : 'en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(ms / 3_600_000);
  return `${value} h`;
}

/** Money in the locale's format ("1.240 €" / "€1,240"). */
export function formatMoney(cents: number, locale: Locale, currency = 'EUR', fractionDigits = 0): string {
  return new Intl.NumberFormat(locale === 'de' ? 'de-DE' : 'en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(cents / 100);
}

/** Duration for screen readers: "1 Stunde 27 Minuten" / "1 hour 27 minutes". */
export function spokenDuration(ms: number, locale: Locale): string {
  const totalMinutes = Math.max(0, Math.floor(ms / 60_000));
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  const words =
    locale === 'de'
      ? { h1: 'Stunde', hn: 'Stunden', m1: 'Minute', mn: 'Minuten' }
      : { h1: 'hour', hn: 'hours', m1: 'minute', mn: 'minutes' };
  const parts: string[] = [];
  if (h > 0) parts.push(`${h} ${h === 1 ? words.h1 : words.hn}`);
  if (m > 0 || h === 0) parts.push(`${m} ${m === 1 ? words.m1 : words.mn}`);
  return parts.join(' ');
}
